const BASE_URL = `${process.env.REACT_APP_BACKEND_API_PREFIX}/api/v1/auth`;

export interface LoginTokens {
  accessToken: string;
  refreshToken: string;
  idToken: string;
}

export type LoginResult =
  | { status: 'success'; tokens: LoginTokens }
  | { status: 'invalid_credentials' }
  | { status: 'password_change_required' }
  | { status: 'error' };

// Bypasses fetchWithHandling on purpose: that helper assumes an existing session (calls
// onSessionExpired / keycloak.logout() on 401), which makes no sense for the login/change-password
// calls — neither has a session yet, so a failure just needs a plain inline error, not a logout of
// a session that never started. Same reasoning as InterventionReportService.submitReport.
const postAuth = async (path: string, payload: object): Promise<LoginResult> => {
  try {
    const response = await fetch(`${BASE_URL}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      return { status: 'invalid_credentials' };
    }

    // The account's credential is temporary (new account, admin password reset, or email change) —
    // Keycloak refuses to hand out a token via this flow until the password is actually changed.
    if (response.status === 403) {
      return { status: 'password_change_required' };
    }

    if (!response.ok) {
      return { status: 'error' };
    }

    const data = await response.json();
    return {
      status: 'success',
      tokens: {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        idToken: data.idToken,
      },
    };
  } catch (error) {
    console.error(`Error during ${path}:`, error);
    return { status: 'error' };
  }
};

export const login = (username: string, password: string): Promise<LoginResult> =>
  postAuth('login', { username, password });

// Called after login() returns 'password_change_required'. On success the backend logs the user in
// with the new password too, so the result carries fresh tokens exactly like login() does.
export const changePassword = (
  username: string,
  oldPassword: string,
  newPassword: string
): Promise<LoginResult> =>
  postAuth('change-password', { username, oldPassword, newPassword });
